import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Grid,
  Input,
  Row,
  Select,
  SelectProps,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context/AuthContext";

import { Cascader } from "antd-mobile";
import { CheckListValue } from "antd-mobile/es/components/check-list";
import { CustomMessage } from "../../../../components";
import { contactMethod } from "../../../../enumerations/contactMethod";
import { genderEnum } from "../../../../enumerations/genderEnum";
import { PhoneCodeResponse } from "../../../../hooks/PhoneCode/data";
import { useGetAreaCode } from "../../../../hooks/PhoneCode/hooks";
import { ProfileResponse } from "../../../../hooks/Profile/data";
import {
  useGetProfile,
  usePostProfile,
} from "../../../../hooks/Profile/profile.hooks";
import useGetTimezone from "../../../../hooks/Timezone/timezone.hooks";
import { UserLevelResponse } from "../../../../hooks/UserLevel/data";
import { useGetUserLevels } from "../../../../hooks/UserLevel/userlevel.hooks";
import { InputRequired } from "../../../../validations/validations";
import styles from "./style.module.scss";
import "./style.scss";

const ProfileEditor: React.FC = () => {
  const [form] = Form.useForm();
  const { userInfo } = useAuth();
  const { t } = useTranslation();
  const DEFAULT_AREA_CODE = "+886";
  const DEFAULT_CONTACT_TYPE = "LINE";
  const { forceDate, toLocalTimezone, toServerTimezone } = useGetTimezone();
  const { data: phoneArea } = useGetAreaCode({});
  const { data: userLevels } = useGetUserLevels({});
  const [loading, setLoading] = useState<boolean>(false);
  const [openSelect, setOpenSelect] = useState<boolean>(false);
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [areaCodeValue, setAreaCodeValue] = useState<CheckListValue[]>([]);
  const [levelOptions, setLevelOptions] = useState<SelectProps["options"]>([]);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const { refetch: refetchProfile } = useGetProfile({
    enabled: false,
  });

  const { mutateAsync: postProfile } = usePostProfile();

  const [areaOptions, setAreaOptions] = useState<any[]>([]);
  const [contactOptions, setContactOptions] = useState<SelectProps["options"]>(
    []
  );
  const [genderOptions, setGenderOptions] = useState<SelectProps["options"]>(
    []
  );

  useEffect(() => {
    if (!userLevels) return;
    setLevelOptions(
      userLevels.map((level: UserLevelResponse) => {
        return {
          label: t(`userlevel.${level.level_code}`),
          value: level.level_code,
        };
      })
    );
  }, [userLevels]);

  useEffect(() => {
    if (!phoneArea || phoneArea.length <= 0) return;
    const _area = phoneArea.map((area: PhoneCodeResponse) => {
      return {
        label: `+${area.phoneCode}`,
        value: area.phoneCode,
      };
    });
    form.setFieldsValue({
      orderer_area_code: 886,
    });
    setAreaOptions(_area);
  }, [phoneArea]);

  useEffect(() => {
    setContactOptions(
      contactMethod.map((contact: any) => {
        return {
          label: t(`normal.contact.${contact.value}`),
          value: contact.value,
        };
      })
    );
    setGenderOptions(
      genderEnum.map((gender: any) => {
        return {
          label: t(`gender.${gender.value}`),
          value: gender.value,
        };
      })
    );
    form.setFieldsValue({
      contact_type: contactMethod[0].value,
    });
  }, []);

  const onUpdateProfile = async (value: any) => {
    const request: ProfileResponse = {
      ...value,
      contact: [
        {
          contact_type: value.contact_type,
          contact_value: value.contact_value,
        },
      ],
      birthday: toServerTimezone(forceDate(value.birthday))?.format(
        "YYYY-MM-DD HH:mm:ss"
      ),
    };
    console.debug("request", request);
    setLoading(true);
    await postProfile(
      {
        params: { ...request },
      },
      {
        onSuccess: (success: any) => {
          // queryClient.invalidateQueries({ queryKey: [queryKey.PROFILE] });
          CustomMessage({ alertMessage: t("message.success.update") });
          refetchProfile();
          setLoading(false);
        },
        onError: () => {
          CustomMessage({
            alertMessage: t("normal.upload.error"),
            alertType: "error",
          });
          setLoading(false);
        },
      }
    );
  };

  useEffect(() => {
    if (!userInfo) return;
    const areaCode = userInfo?.area_code
      ? userInfo.area_code.toString()
      : DEFAULT_AREA_CODE;
    form.setFieldsValue({
      ...userInfo,
      area_code: areaCode,
      contact_type:
        userInfo?.contact && userInfo.contact.length > 0
          ? userInfo?.contact[0].contact_type
          : DEFAULT_CONTACT_TYPE,
      contact_value:
        userInfo?.contact && userInfo.contact.length > 0
          ? userInfo?.contact[0].contact_value
          : undefined,
      birthday: toLocalTimezone(userInfo?.birthday),
    });
    setAreaCodeValue([areaCode]);
  }, [userInfo]);

  const handleClickAreaCode = () => {
    if (screens.sm) {
      setOpenSelect(!openSelect);
      setOpenPopup(false);
    } else {
      setOpenSelect(false);
      setOpenPopup(true);
    }
  };

  const handleConfirmAreaCode = (val: CheckListValue[]) => {
    console.debug(val);
    if (val.length > 0) {
      form.setFieldsValue({
        area_code: val[0],
      });
      setAreaCodeValue(val);
    }
  };

  const contactSelector = (
    <Form.Item name={"contact_type"} style={{ marginBottom: 0 }}>
      <Select style={{ width: 100 }} options={contactOptions} />
    </Form.Item>
  );

  const areaSelector = (
    <Form.Item name={"area_code"} style={{ marginBottom: 0 }}>
      <Select
        style={{ width: 100 }}
        options={areaOptions}
        open={openSelect}
        onClick={handleClickAreaCode}
        onChange={(val) => setAreaCodeValue([val])}
      />
    </Form.Item>
  );

  return (
    <Card
      className={styles.orderCard}
      variant="borderless"
      title={t("profile.title")}
      size={screens.xs ? "small" : "default"}
    >
      <Form
        requiredMark={true}
        form={form}
        layout={"vertical"}
        onFinish={onUpdateProfile}
      >
        <div
          className={`${styles.container} ${screens.md ? "" : styles.small}`}
          style={{ gap: screens.xs ? "4px" : "24px" }}
        >
          <div style={{ flex: 1, width: `${screens.md ? "auto" : "100%"}` }}>
            <Row gutter={[13, 16]}>
              <Col span={screens.md ? 12 : 24}>
                <Form.Item
                  name="name"
                  label={t("profile.firstname.title")}
                  rules={[InputRequired(t, "profile.firstname.title")]}
                >
                  <Input placeholder={t("profile.firstname.hint")} />
                </Form.Item>
              </Col>
              <Col span={screens.md ? 12 : 24}>
                <Form.Item
                  name="nickname"
                  label={t("profile.nickname.title")}
                  rules={[InputRequired(t, "profile.nickname.title")]}
                >
                  <Input placeholder={t("profile.nickname.hint")} />
                </Form.Item>
              </Col>
              <Col span={screens.md ? 12 : 24}>
                <Form.Item name="email" label={t("profile.email.title")}>
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={screens.md ? 12 : 24}>
                <Form.Item
                  name="mobile"
                  label={t("checkout.phone.title")}
                  rules={[
                    InputRequired(t, "checkout.phone.title"),
                    { min: 8, message: t("error.mobile.limit") },
                    { pattern: /^\d+$/, message: t("error.mobile.numberonly") },
                  ]}
                >
                  <Input
                    addonBefore={areaSelector}
                    placeholder={t("checkout.phone.hint")}
                  />
                </Form.Item>
              </Col>
              <Col span={screens.md ? 12 : 24}>
                <Form.Item
                  name="birthday"
                  label={t("profile.birthday.title")}
                  rules={[InputRequired(t, "profile.birthday.title")]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder={t("profile.birthday.hint")}
                  />
                </Form.Item>
              </Col>
              <Col span={screens.md ? 12 : 24}>
                <Form.Item
                  name="contact_value"
                  label={t("checkout.contact.title")}
                  rules={[
                    InputRequired(t, "checkout.contact.title"),
                    {
                      pattern: /^[\x20-\x7E]*$/, // ASCII range: space to tilde
                      message: t("error.contact.format"),
                    },
                  ]}
                >
                  <Input
                    addonBefore={contactSelector}
                    placeholder={t("checkout.contact.hint")}
                  />
                </Form.Item>
              </Col>
              <Col span={screens.md ? 12 : 24}>
                <Form.Item
                  name={"gender"}
                  label={t("profile.gender.title")}
                  rules={[InputRequired(t, "profile.gender.title")]}
                >
                  <Select
                    options={genderOptions}
                    placeholder={t("profile.gender.hint")}
                  />
                </Form.Item>
              </Col>
              {/* <Col span={screens.md ? 12 : 24}>
                <Form.Item
                  name={`level_code`}
                  label={t("checkout.data.skill.title")}
                  initialValue={userLevels ? userLevels[0].level_code : ""}
                >
                  <Select options={levelOptions} />
                </Form.Item>
              </Col> */}
              <Col span={24}>
                <div className={styles.buttonContainer}>
                  <Button
                    loading={loading}
                    block={screens.md ? false : true}
                    type={"primary"}
                    htmlType={"submit"}
                  >
                    {t("normal.save")}
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </div>
        <Cascader
          options={areaOptions}
          visible={openPopup}
          onClose={() => {
            setOpenPopup(false);
          }}
          value={areaCodeValue}
          onConfirm={handleConfirmAreaCode}
          confirmText={t("normal.confirm")}
          cancelText={t("normal.cancel")}
          placeholder={t("normal.pleaseSelect")}
        />
      </Form>
    </Card>
  );
};

export default ProfileEditor;
